import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Check, FileText, HardDrive, Image as ImageIcon, Languages,
  Newspaper, Plus, Search,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn, formatBytes } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useOverview } from '@/lib/queries';
import { PageHeader } from '@/components/PageHeader';
import { Chip } from '@/components/ui/Chip';
import { Skeleton } from '@/components/ui/Feedback';

/**
 * Not vanity charts (§10.3). Four things, in this order: what needs my
 * attention, what changed recently, quick actions, and system state.
 *
 * The attention list is the whole point — it is the difference between a
 * dashboard you glance at and one you act on. Every row is a filtered link
 * into the list that fixes it.
 */
export function Overview() {
  const { t, formatRelative } = useI18n();
  const { user, isSuperAdmin } = useAuth();
  // The recent-changes feed is restricted — super admin only.
  const isAdmin = isSuperAdmin;
  const { data, isLoading } = useOverview();

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? t.overview.greetingMorning : hour < 18 ? t.overview.greetingDay : t.overview.greetingEvening;

  const attention = data?.attention;
  const items: Array<{ key: string; count: number; label: string; to: string; icon: LucideIcon }> = attention
    ? [
        { key: 'drafts', count: attention.drafts, label: t.overview.drafts, to: '/articles?status=DRAFT', icon: FileText },
        { key: 'missingRu', count: attention.missingRu, label: t.overview.missingRu, to: '/articles?missing=ru', icon: Languages },
        { key: 'missingSeo', count: attention.missingSeo, label: t.overview.missingSeo, to: '/articles?missing=seo', icon: Search },
        { key: 'noCover', count: attention.noCover, label: t.overview.noCover, to: '/articles?missing=cover', icon: ImageIcon },
        { key: 'missingEn', count: attention.missingEn, label: t.overview.missingEn, to: '/articles?missing=en', icon: Languages },
        // Murojaatlar (leads) hozircha yashirilgan.
      ].filter((item) => item.count > 0)
    : [];

  return (
    <>
      <PageHeader
        title={`${greeting}${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        subtitle={t.overview.title}
        action={
          <Link
            to="/articles/new"
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-control bg-brand-500 px-4 text-ui font-medium text-white transition-ui duration-instant hover:bg-brand-600 active:bg-brand-700"
          >
            <Plus aria-hidden className="h-4 w-4" />
            {t.overview.newArticle}
          </Link>
        }
      />

      <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-6 px-6 pb-12 lg:grid-cols-[1.4fr_1fr]">
        <section className="flex flex-col gap-6">
          <div className="card p-6">
            <h2 className="eyebrow mb-4">{t.overview.attention}</h2>

            {isLoading ? (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-control" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex items-start gap-3 py-2">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-chip bg-grass-400/12 text-grass-500">
                  <Check aria-hidden className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-body font-medium text-strong">{t.overview.nothingToDo}</p>
                  <p className="mt-0.5 text-ui text-muted">{t.overview.nothingToDoHint}</p>
                </div>
              </div>
            ) : (
              <ul className="-mx-2 flex flex-col">
                {items.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      className="row group flex items-center gap-3 rounded-control px-2 py-2"
                    >
                      {/* A typed icon makes the row scannable — the eye reads
                          "language", "SEO", "cover" from the glyph before the
                          words. Amber throughout: everything here is a soft
                          warning, nothing is an error. */}
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-amber-400/12 text-amber-600 ring-1 ring-inset ring-amber-400/20 dark:text-amber-400">
                        <item.icon aria-hidden className="h-[1.0625rem] w-[1.0625rem]" strokeWidth={1.85} />
                      </span>
                      <span className="flex-1 text-body text-strong">{item.label}</span>
                      <Chip tone="amber" mono>
                        {item.count}
                      </Chip>
                      <ArrowRight
                        aria-hidden
                        className="h-4 w-4 shrink-0 -translate-x-1 text-faint opacity-0 transition-ui duration-quick ease-out group-hover:translate-x-0 group-hover:opacity-100"
                        strokeWidth={1.85}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isAdmin && (
            <div className="card p-6">
              <h2 className="eyebrow mb-4">{t.overview.recent}</h2>
              {isLoading ? (
                <div className="flex flex-col gap-3">
                  {Array.from({ length: 4 }, (_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : data && data.recent.length > 0 ? (
                <ul className="flex flex-col divide-y divide-line">
                  {data.recent.slice(0, 8).map((entry) => (
                    <li key={entry.id} className="flex items-baseline gap-2 py-2.5 text-ui">
                      <span className="font-medium text-strong">{entry.user?.name ?? 'Tizim'}</span>
                      <span className="min-w-0 flex-1 truncate text-muted">
                        {t.audit.actions[entry.action] ?? entry.action}
                        {entry.entityId && <span className="ml-1 font-mono text-code text-faint">#{entry.entityId}</span>}
                      </span>
                      <span className="shrink-0 text-faint">{formatRelative(entry.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-2 text-ui text-muted">{t.overview.noRecent}</p>
              )}
            </div>
          )}
        </section>

        <section className="flex flex-col gap-6">
          <div className="card p-6">
            <h2 className="eyebrow mb-4">{t.overview.quickActions}</h2>
            <div className="flex flex-col gap-2">
              <QuickAction to="/articles/new" icon={<Newspaper className="h-[1.0625rem] w-[1.0625rem]" strokeWidth={1.85} />} label={t.overview.newArticle} />
              <QuickAction to="/media" icon={<ImageIcon className="h-[1.0625rem] w-[1.0625rem]" strokeWidth={1.85} />} label={t.overview.openMedia} />
            </div>
          </div>

          <div className="card p-6">
            <h2 className="eyebrow mb-4">{t.overview.system}</h2>
            {isLoading || !data ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <dl className="flex flex-col gap-3 text-ui">
                <div className="flex items-center justify-between gap-3">
                  <dt className="flex items-center gap-2 text-muted">
                    <HardDrive aria-hidden className="h-3.5 w-3.5" />
                    {t.overview.storage}
                  </dt>
                  <dd
                    className={cn(
                      'flex items-center gap-1.5 font-medium',
                      data.system.storage.healthy ? 'text-grass-500' : 'text-accent-600 dark:text-accent-400',
                    )}
                  >
                    {!data.system.storage.healthy && <AlertTriangle aria-hidden className="h-3.5 w-3.5" />}
                    {data.system.storage.healthy ? t.overview.storageOk : t.overview.storageDown}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted">{t.overview.mediaCount}</dt>
                  <dd className="font-mono tabular-nums text-strong">
                    {data.system.media.count} · {formatBytes(data.system.media.bytes)}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted">{t.overview.mediaUnused}</dt>
                  <dd className="font-mono tabular-nums text-strong">
                    <Link to="/media?filter=unused" className="hover:text-brand-500">
                      {data.system.media.unused}
                    </Link>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-line pt-3">
                  <dt className="text-muted">{t.overview.published}</dt>
                  <dd className="font-mono tabular-nums text-strong">{data.content.published}</dd>
                </div>
              </dl>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

function QuickAction({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-control border border-line bg-surface px-2.5 py-2 text-ui font-medium text-strong transition-ui duration-instant hover:border-line-strong hover:bg-raised dark:hover:border-brand-500/60"
    >
      {/* Brand-tinted square: the whole app's colour language is two-tone —
          amber marks what needs attention, brand marks what to go do. */}
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-500/10 text-brand-600 ring-1 ring-inset ring-brand-500/15 dark:text-brand-300">
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      <ArrowRight
        aria-hidden
        className="h-4 w-4 shrink-0 -translate-x-1 text-faint opacity-0 transition-ui duration-quick ease-out group-hover:translate-x-0 group-hover:opacity-100"
        strokeWidth={1.85}
      />
    </Link>
  );
}
